import React from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import UniversalLink from '@plone/volto/components/manage/UniversalLink/UniversalLink';
import { flattenToAppURL } from '@plone/volto/helpers';
import { getContent } from '@plone/volto/actions';
import Icon from '@plone/volto/components/theme/Icon/Icon';
import arrowRightSVG from 'fcmh/theme/assets/icons/arrow-right.svg';
import { useIntl } from 'react-intl';
import messages from '../../../messages';

const safe = (s) => (typeof s === 'string' ? s : '');

const resolveLink = (val) => {
  if (!val) return '';
  if (typeof val === 'string') return val;
  if (Array.isArray(val)) return resolveLink(val[0]);
  if (val['@id']) return flattenToAppURL(val['@id']);
  if (val.url) return val.url;
  return '';
};

const getImageURL = (item) => {
  if (!item) return null;

  try {
    const previewImageLink = item?.preview_image_link;
    if (previewImageLink?.image_scales?.image?.[0]?.base_path) {
      const origin =
        typeof window !== 'undefined' ? window.location.origin : '';
      return `${origin}${previewImageLink.image_scales.image[0].base_path}/@@images/image`;
    }

    const scales = item?.image_scales?.preview_image_link;
    if (Array.isArray(scales) && scales[0]?.base_path) {
      const origin =
        typeof window !== 'undefined' ? window.location.origin : '';
      return `${origin}${scales[0].base_path}/@@images/image`;
    }

    const base = flattenToAppURL(item?.['@id'] || item?.getURL || '');
    if (base) {
      const origin =
        typeof window !== 'undefined' ? window.location.origin : '';
      return `${origin}${base}/@@images/preview_image/preview`;
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[FCMH GridVariation] getImageURL failed', e);
  }

  return null;
};

const extractPreviewCopyright = (item) => {
  if (!item) return '';

  const direct =
    item.preview_caption ||
    item.previewCaption ||
    item.preview_image_caption ||
    item.previewImageCaption ||
    item.preview_image_legend ||
    item.previewImageLegend ||
    item.image_caption ||
    item.imageCaption ||
    item.legend ||
    item.rights ||
    item.copyright ||
    '';

  const nested =
    item.preview_image?.caption ||
    item.preview_image?.legend ||
    item.preview_image?.rights ||
    item.preview_image?.copyright ||
    '';

  const val = direct || nested;

  return safe(val).trim();
};

const GridCard = ({ item, index, isEditMode }) => {
  const intl = useIntl();
  const dispatch = useDispatch();

  const rawId = item?.['@id'] || item?.getURL || '';
  const itemUrl = rawId ? flattenToAppURL(rawId) : '#';
  const imageSrc = getImageURL(item);

  const appURL = rawId ? flattenToAppURL(rawId) : null;

  const sub = useSelector((state) =>
    appURL ? state.content.subrequests?.[appURL] : null,
  );
  const sourceContent = sub?.data;

  React.useEffect(() => {
    if (appURL && !sourceContent && !sub?.loading) {
      dispatch(getContent(appURL, null, appURL));
    }
  }, [appURL, sourceContent, sub?.loading, dispatch]);

  const copyrightText =
    extractPreviewCopyright(sourceContent) || extractPreviewCopyright(item);

  const [copyrightOpen, setCopyrightOpen] = React.useState(false);

  const renderCopyright = () => {
    if (!copyrightText) return null;

    const toggle = (e) => {
      e?.preventDefault?.();
      e?.stopPropagation?.();
      setCopyrightOpen((o) => !o);
    };

    return (
      <div
        className={`listing-grid-card__copyright${
          copyrightOpen ? ' is-open' : ''
        }`}
        onMouseLeave={() => setCopyrightOpen(false)}
      >
        <button
          type="button"
          className="listing-grid-card__copyright-icon"
          aria-label="Bildnachweis anzeigen"
          aria-expanded={copyrightOpen}
          onClick={toggle}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.stopPropagation();
              setCopyrightOpen(false);
            }
          }}
        >
          ©
        </button>

        <div className="listing-grid-card__copyright-tooltip" role="tooltip">
          <span className="listing-grid-card__copyright-text">
            {copyrightText}
          </span>
        </div>
      </div>
    );
  };

  return (
    <article className="listing-grid-card" key={rawId || index}>
      <div className="listing-grid-card__media">
        {imageSrc ? (
          <img
            src={imageSrc}
            alt={item?.title || ''}
            className="listing-grid-card__img"
            loading="lazy"
          />
        ) : (
          <span className="listing-grid-card__placeholder" aria-hidden />
        )}
        <span className="listing-grid-card__media-gradient" aria-hidden />
        {renderCopyright()}
      </div>

      <div className="listing-grid-card__body">
        <h3 className="listing-grid-card__title">{item?.title || 'Eintrag'}</h3>

        {item?.description ? (
          <p className="listing-grid-card__description">{item.description}</p>
        ) : null}

        <UniversalLink
          href={itemUrl}
          className="listing-grid-card__link"
          aria-label={`Weiterlesen: ${item?.title || 'Eintrag'}`}
          disabled={isEditMode}
        >
          <Icon name={arrowRightSVG} size="16px" />
          <span>{intl.formatMessage(messages.readMore)}</span>
        </UniversalLink>
      </div>
    </article>
  );
};

GridCard.propTypes = {
  item: PropTypes.any,
  index: PropTypes.number,
  isEditMode: PropTypes.bool,
};

const GridVariation = ({
  items = [],
  isEditMode = false,
  headline,
  moreLinkHref,
  moreLinkLabel,
  styleVariant = 'default',
}) => {
  const footerHref = resolveLink(moreLinkHref);
  const intl = useIntl();

  return (
    <section
      className={`listing-grid-section${
        styleVariant === 'light' ? ' listing-grid-section--light' : ''
      }`}
      aria-labelledby={headline ? 'listing-grid-heading' : undefined}
    >
      {headline ? (
        <h2 id="listing-grid-heading" className="listing-grid-section__heading">
          {headline}
        </h2>
      ) : null}

      <div className="listing-grid">
        {items.map((item, index) => (
          <GridCard
            item={item}
            index={index}
            isEditMode={isEditMode}
            key={item?.['@id'] || item?.getURL || index}
          />
        ))}
      </div>

      {footerHref && moreLinkLabel && (
        <div className="listing-grid-section__footer">
          <UniversalLink
            href={footerHref}
            className="listing-grid-section__more"
          >
            {moreLinkLabel}
          </UniversalLink>
        </div>
      )}
    </section>
  );
};

GridVariation.propTypes = {
  items: PropTypes.arrayOf(PropTypes.any),
  isEditMode: PropTypes.bool,
  headline: PropTypes.string,
  moreLinkHref: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
    PropTypes.array,
  ]),
  moreLinkLabel: PropTypes.string,
  styleVariant: PropTypes.oneOf(['default', 'light']),
};

export default GridVariation;
